'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Image } from '@udemy-v2/react-core-components';
import { useI18n } from '@udemy-v2/i18n';
import { useMatchMedia } from '@udemy-v2/design-system-utils';
import classNames from 'classnames';
import { InstructorCommunityBannerInfoByInstructorIdQuery } from '@/generated/graphql';
import styles from './instructor-community-banner.module.scss';
export type InstructorCommunityBannerInfo = NonNullable<InstructorCommunityBannerInfoByInstructorIdQuery['instructorCommunityBannerInfoByInstructorId']>;
const BANNER_TEXT_COLOR = {
  DARK: 'var(--applied-text-default)',
  LIGHT: '#E9EAF2'
} as const;
export interface InstructorCommunityBannerProps {
  instructorName: string;
  instructorUrl?: string;
  bannerInfo: InstructorCommunityBannerInfo;
}
export function InstructorCommunityBanner(t0) {
  const $ = _c(51);
  const {
    instructorName,
    instructorUrl,
    bannerInfo
  } = t0;
  const {
    gettext,
    interpolate
  } = useI18n();
  const isMobile = useMatchMedia("sm-max");
  const bannerImageUrl = isMobile ? bannerInfo.mobileCommunityBannerImage : bannerInfo.communityBannerImage;
  if (!bannerImageUrl) {
    return null;
  }
  const textColor = isMobile ? BANNER_TEXT_COLOR.LIGHT : BANNER_TEXT_COLOR[bannerInfo.communityBannerTextColorPreference ?? "DARK"];
  const isLightText = textColor === BANNER_TEXT_COLOR.LIGHT;
  let t1;
  if ($[0] !== gettext || $[1] !== instructorName || $[2] !== interpolate) {
    t1 = interpolate(gettext("Join %(instructorName)s's Learning Community"), {
      instructorName
    }, true);
    $[0] = gettext;
    $[1] = instructorName;
    $[2] = interpolate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const bannerTitle = t1;
  let t2;
  if ($[4] !== bannerInfo.isSubscribed || $[5] !== gettext) {
    t2 = bannerInfo.isSubscribed ? gettext("Go to community") : gettext("Join my community");
    $[4] = bannerInfo.isSubscribed;
    $[5] = gettext;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const primaryButtonText = t2;
  const primaryButtonHref = bannerInfo.isSubscribed ? bannerInfo.communityUrl : bannerInfo.communityPaywallUrl;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = classNames("ud-heading-lg", styles["banner-title"]);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== bannerTitle) {
    t4 = <h3 className={t3}>{bannerTitle}</h3>;
    $[8] = bannerTitle;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== bannerInfo.description) {
    t5 = bannerInfo.description && <p className={classNames("ud-text-sm", styles["banner-description"])}>{bannerInfo.description}</p>;
    $[10] = bannerInfo.description;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = !isMobile;
  let t7;
  if ($[12] !== t6) {
    t7 = classNames(styles["banner-buttons"], {
      [styles["banner-buttons-desktop"]]: t6
    });
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== primaryButtonHref || $[15] !== primaryButtonText) {
    t8 = <Button udStyle="primary" size="small" componentClass="a" href={primaryButtonHref}>{primaryButtonText}</Button>;
    $[14] = primaryButtonHref;
    $[15] = primaryButtonText;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = isLightText ? "knockout-outline" : "secondary";
  let t10;
  if ($[17] !== gettext) {
    t10 = gettext("Learn more");
    $[17] = gettext;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== instructorUrl || $[20] !== t10 || $[21] !== t9) {
    t11 = <Button udStyle={t9} size="small" componentClass="a" href={instructorUrl}>{t10}</Button>;
    $[19] = instructorUrl;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t7 || $[25] !== t8) {
    t12 = <div className={t7}>{t8}{t11}</div>;
    $[23] = t11;
    $[24] = t7;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t4 || $[29] !== t5) {
    t13 = <>{t4}{t5}{t12}</>;
    $[27] = t12;
    $[28] = t4;
    $[29] = t5;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const bannerContent = t13;
  if (isMobile) {
    let t14;
    if ($[31] !== bannerImageUrl) {
      t14 = <div className={styles["banner-photo-mobile"]}><Image src={bannerImageUrl} alt="" /></div>;
      $[31] = bannerImageUrl;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    let t15;
    if ($[33] !== textColor) {
      t15 = {
        color: textColor
      };
      $[33] = textColor;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== bannerContent || $[36] !== t15) {
      t16 = <div className={styles["banner-content-mobile"]} style={t15}>{bannerContent}</div>;
      $[35] = bannerContent;
      $[36] = t15;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    let t17;
    if ($[38] !== t14 || $[39] !== t16) {
      t17 = <div className={styles["banner-container-mobile"]} data-purpose="instructor-community-banner-mobile">{t14}{t16}</div>;
      $[38] = t14;
      $[39] = t16;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    return t17;
  }
  const t14 = `url(${bannerImageUrl})`;
  let t15;
  if ($[41] !== t14) {
    t15 = {
      backgroundImage: t14
    };
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== textColor) {
    t16 = {
      color: textColor
    };
    $[43] = textColor;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== bannerContent || $[46] !== t16) {
    t17 = <div className={styles["banner-content"]} style={t16}>{bannerContent}</div>;
    $[45] = bannerContent;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t15 || $[49] !== t17) {
    t18 = <div className={styles["banner-container"]} style={t15} data-purpose="instructor-community-banner">{t17}</div>;
    $[48] = t15;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  return t18;
}